import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import type { AdminRole, ClaimStatus } from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import { useClaimFiltersStore } from '@/stores/useClaimFiltersStore';
import { ClaimsTable } from '../components/ClaimsTable';
import { ClaimFiltersBar } from '../components/ClaimFiltersBar';
import { ClaimBulkActionDialog, type ClaimBulkAction, type ClaimBulkTarget } from '../components/ClaimBulkActionDialog';
import { useClaimsQuery } from '../hooks/useClaimsQuery';
import { canPerformClaimAction } from '../lib/claimActionRbac';
import { CLAIM_EXPORT_ROLES, downloadClaimsExcel } from '../lib/claimExport';
import { toast } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';

export const ClaimListPage = () => {
  const { t } = useTranslation('claims');
  const { currentAdmin } = useAuthContext();
  const roles = useMemo<ReadonlyArray<AdminRole>>(() => currentAdmin?.roles ?? [], [currentAdmin]);

  const q = useClaimFiltersStore((s) => s.q);
  const searchType = useClaimFiltersStore((s) => s.searchType);
  const orderDateFrom = useClaimFiltersStore((s) => s.orderDateFrom);
  const orderDateTo = useClaimFiltersStore((s) => s.orderDateTo);
  const claimStatus = useClaimFiltersStore((s) => s.claimStatus);
  const claimType = useClaimFiltersStore((s) => s.claimType);
  const reasonCode = useClaimFiltersStore((s) => s.reasonCode);
  const dateFrom = useClaimFiltersStore((s) => s.dateFrom);
  const dateTo = useClaimFiltersStore((s) => s.dateTo);
  const page = useClaimFiltersStore((s) => s.page);
  const size = useClaimFiltersStore((s) => s.size);
  const sort = useClaimFiltersStore((s) => s.sort);
  const setPage = useClaimFiltersStore((s) => s.setPage);
  const setSort = useClaimFiltersStore((s) => s.setSort);

  const query = useClaimsQuery({
    page,
    size,
    sort,
    q,
    searchType,
    claimStatus,
    claimType,
    reasonCode,
    dateFrom,
    dateTo,
    orderDateFrom,
    orderDateTo,
  });

  const items = query.data?.items ?? [];

  const canExport = CLAIM_EXPORT_ROLES.some((r) => roles.includes(r));
  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    setExporting(true);
    try {
      await downloadClaimsExcel({ q, searchType, claimStatus, claimType, reasonCode, dateFrom, dateTo, orderDateFrom, orderDateTo });
    } catch (e) {
      toast.error(friendlyMessage(e, '클레임 엑셀을 내려받지 못했습니다.'));
    } finally {
      setExporting(false);
    }
  };
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;

  // QA-530 일괄 승인·반려 — 주문 관리처럼 체크박스로 고르고 하단 바에서 처리. 페이지·조건이 바뀌면 선택을 비운다.
  const canApprove = canPerformClaimAction('approve', roles);
  const canReject = canPerformClaimAction('reject', roles);
  const selectable = canApprove || canReject;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<ClaimBulkAction | null>(null);
  const [bulkTargets, setBulkTargets] = useState<ClaimBulkTarget[]>([]);
  useEffect(() => {
    setSelectedIds([]);
  }, [page, size, sort, q, searchType, claimStatus, claimType, reasonCode, dateFrom, dateTo, orderDateFrom, orderDateTo]);

  const openBulk = (action: ClaimBulkAction) => {
    setBulkTargets(
      items
        .filter((c) => selectedIds.includes(c.claimId))
        .map((c) => ({ claimId: c.claimId, orderNo: c.orderNo ?? null, claimStatus: (c.claimStatus as ClaimStatus | undefined) ?? null })),
    );
    setBulkAction(action);
  };

  return (
    <PageShell
      title={t('claims.list.title')}
      actions={
        // QA-607 — 지금 검색 조건 그대로 엑셀(신청 상품마다 한 줄). 권한: OPS_ADMIN·CS_AGENT·SUPER_ADMIN.
        canExport ? (
          <Button type="button" size="sm" onClick={() => void onExport()} disabled={exporting} data-testid="claims-export-button">
            {exporting ? '엑셀 만드는 중…' : '엑셀 다운로드'}
          </Button>
        ) : undefined
      }
    >
      <ClaimFiltersBar />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('claims.list.error')}</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('claims.list.retry')}
          </Button>
        </div>
      ) : (
        <ClaimsTable
          items={items}
          sort={sort}
          onSortChange={setSort}
          {...(selectable
            ? {
                selectedIds,
                onToggleRow: (id: string) =>
                  setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
                onToggleAll: (ids: string[]) => setSelectedIds(ids),
              }
            : {})}
        />
      )}

      <Pagination
        total={totalItems ?? 0}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
        className="mt-4"
      />

      {selectable && selectedIds.length > 0 ? (
        <div
          role="region"
          aria-label={t('claims.bulk.label', '선택한 클레임 일괄 처리')}
          className="sticky bottom-4 z-10 mt-4 flex items-center gap-3 rounded-xl px-4 py-3 shadow"
          style={{ background: 'var(--surface-raised)' }}
          data-testid="claims-bulk-footer"
        >
          <span className="text-sm text-[color:var(--content-strong)]">
            {t('claims.bulk.selected', { count: selectedIds.length, defaultValue: '{{count}}건 선택' })}
          </span>
          {canApprove ? (
            <Button type="button" variant="cta" size="sm" onClick={() => openBulk('approve')} data-testid="claims-bulk-approve">
              {t('claims.bulk.approve', '일괄 승인')}
            </Button>
          ) : null}
          {canReject ? (
            <Button type="button" variant="outline" size="sm" onClick={() => openBulk('reject')} data-testid="claims-bulk-reject">
              {t('claims.bulk.reject', '일괄 반려')}
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={() => setSelectedIds([])}>
            {t('claims.bulk.clear', '선택 해제')}
          </Button>
        </div>
      ) : null}

      <ClaimBulkActionDialog
        action={bulkAction}
        targets={bulkTargets}
        onClose={(done) => {
          setBulkAction(null);
          if (done) setSelectedIds([]);
        }}
      />
    </PageShell>
  );
};
